/**
 * FIX-TZ: an Israeli wall-clock date + time -> the exact instant, independent of the
 * browser's / OS timezone and of the server's. Pure (Intl only), DST-aware.
 */
export const OTE_TZ = 'Asia/Jerusalem';

function offsetMinutes(at: Date, tz: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(at);
  const g = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? '0');
  const asUtc = Date.UTC(g('year'), g('month') - 1, g('day'), g('hour') % 24, g('minute'), g('second'));
  return Math.round((asUtc - Math.floor(at.getTime() / 1000) * 1000) / 60000);
}

/** UTC ISO string of `date` (YYYY-MM-DD) + `time` (HH:mm) read as Israeli time; null when invalid. */
export function israelWallToIso(date: string, time: string, tz: string = OTE_TZ): string | null {
  const d = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  const t = /^(\d{2}):(\d{2})$/.exec(time);
  if (!d || !t || Number(t[1]) > 23 || Number(t[2]) > 59) return null;
  const naive = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]), 0);
  let guess = naive - offsetMinutes(new Date(naive), tz) * 60000;
  guess = naive - offsetMinutes(new Date(guess), tz) * 60000;
  const out = new Date(guess);
  return Number.isNaN(out.getTime()) ? null : out.toISOString();
}

/** "HH:mm" of an instant on the Israeli clock (not the browser's). '' when invalid. */
export function israelHhmm(iso: string | Date | null | undefined): string {
  if (!iso) return '';
  const d = iso instanceof Date ? iso : new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone: OTE_TZ, hourCycle: 'h23', hour: '2-digit', minute: '2-digit' }).formatToParts(d);
  const g = (t: string) => parts.find((p) => p.type === t)?.value ?? '00';
  return `${g('hour')}:${g('minute')}`;
}

/** Today's calendar day ('YYYY-MM-DD') on the Israeli clock - not the UTC day `toISOString()` gives. */
export function israelToday(now: Date = new Date()): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: OTE_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);
}

/** `datetime-local` value ("YYYY-MM-DDTHH:mm") of an instant on the Israeli clock; '' when empty/invalid. */
export function isoToIsraelInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${israelToday(d)}T${israelHhmm(d)}`;
}

/** The exact instant (UTC ISO) of a `datetime-local` value read as Israeli time; null when empty/invalid. */
export function israelInputToIso(v: string | null | undefined): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(v ?? '');
  return m ? israelWallToIso(m[1], m[2]) : null;
}

/**
 * DQ-445 / DQ-446: a DATE column (due date, `2026-10-29` or `2026-10-29T00:00:00.000Z`) is
 * overdue only once its calendar day is before today's ISRAELI day - same as the server's
 * `due_date < CURRENT_DATE`. Comparing the instant to `new Date()` marked it late from 02:00 on its own day.
 */
export function dueDayPassed(due: string | null | undefined, now: Date = new Date()): boolean {
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(due ?? ''));
  return !!m && m[1] < israelToday(now);
}
